import { Alert, Flex, Select, TableColumnsType, Tag } from 'antd';
import * as Component from 'components';
import {
  TransferDataHandlerInterface,
  TransferDataSourceInterface,
} from 'components/transfer/Transfer';
import { ContentInterface } from 'content/type';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { contentApiService } from 'service/api/contentApiService';
import { playlistContentListService } from '../services/playlistContentListService';

/**
 * Content types worth ordering shorts from — a work order needs a full-length source video.
 * `trailer`/`clip`/`shorts` are already short, and `live_streaming` has no stored mp4.
 */
const SOURCE_CONTENT_TYPE_OPTIONS = [
  { value: 'tv-series,movie', label: 'Episodes + Movies' },
  { value: 'tv-series', label: 'Episodes only' },
  { value: 'movie', label: 'Movies only' },
];

const DEFAULT_CONTENT_TYPES = SOURCE_CONTENT_TYPE_OPTIONS[0].value;

/**
 * Picks individual contents (episodes **or movies**) for the playlist — the `content` rule.
 *
 * Series/season rules only reach episodes that hang off a season, so a standalone movie is
 * unreachable through them (`Content.season` is nullable).
 *
 * Reuses `playlistContentListService`, the same path the manual playlist screen uses:
 * - the selected pane reads `/api/v1/playlist-content/?playlist_id=`, i.e. what the playlist's
 *   rules actually select — the feed's own view of itself
 * - moving an item **persists immediately** through `upsert-rule` (the transfer's confirm
 *   dialog already says so), touching only the `content` rule and leaving series/season alone
 *
 * So this section is not part of the form the save button submits.
 */
export const ContentRule: React.FC<{
  /** Adds the work-order caveats — the bebridge feed narrows this selection further. */
  isWorkOrder?: boolean;
}> = ({ isWorkOrder }) => {
  const { id } = useParams<{ id: string }>();
  const [contentTypes, setContentTypes] = useState<string>(DEFAULT_CONTENT_TYPES);

  const columns: TableColumnsType<TransferDataSourceInterface> = [
    {
      dataIndex: 'id',
      title: 'Id',
      width: '10%',
    },
    {
      dataIndex: 'name',
      title: 'Name',
      render: (_, record): React.ReactNode => (
        <Flex align="center" gap="small">
          <span>{record.name}</span>
        </Flex>
      ),
    },
    {
      dataIndex: 'content_type',
      title: 'Type',
      width: '20%',
      render: (value: string): React.ReactNode => (
        <Tag color={value === 'movie' ? 'purple' : 'blue'}>{value}</Tag>
      ),
    },
  ];

  const filterOption = (input: string, item: ContentInterface): boolean =>
    item.title?.includes(input);

  const mapToTransferItem = (content: ContentInterface): TransferDataSourceInterface => ({
    ...content,
    key: content.id,
    name: content.title,
    disabled: false,
    poster: undefined,
  });

  const dataHandler: TransferDataHandlerInterface = {
    fetchSourceData: async (query: Record<string, string>) => {
      const extendedQuery = new URLSearchParams(query);
      extendedQuery.set('content_type', contentTypes);
      const { results, count } = await contentApiService.fetch(extendedQuery);
      return {
        data: results.map(mapToTransferItem),
        count,
      };
    },
    fetchTargetData: async (query: Record<string, string>) => {
      // Everything this playlist's rules select, not just the ids of the content rule —
      // this is the list the feed is built from.
      const { results, count } = await playlistContentListService.fetch(
        new URLSearchParams({ ...query, playlist_id: id! })
      );
      return {
        data: results.map(mapToTransferItem),
        count,
      };
    },
    createWithParentId: async (ids: number[]) => {
      await playlistContentListService.createContentListInPlaylist(Number(id), ids);
    },
    deleteWithParentId: async (ids: number[]) => {
      await playlistContentListService.deleteContentListInPlaylist(Number(id), ids);
    },
  };

  return (
    <div>
      <h3 style={{ display: 'flex', alignItems: 'center' }}>
        <span>Content (Episode / Movie)</span>
        <span
          style={{
            flex: 1,
            borderBottom: '1px solid #666',
            marginLeft: '8px',
            height: '0.5em',
          }}
        />
      </h3>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: '12px' }}
        message="Moving an item here saves right away — the save button below is for the other rules."
        description={
          <>
            Pick individual episodes or movies; series and season rules only reach episodes that
            belong to a season. The right-hand list shows{' '}
            <b>everything this playlist&apos;s rules select</b>, so an episode that came from a
            series or season rule appears here too — remove it from that rule rather than from this
            list.
            {isWorkOrder && (
              <>
                {' '}
                The work order feed then keeps only items whose <b>On Air Date</b> falls inside its
                window; a movie with no On Air Date never reaches the vendor.
              </>
            )}
          </>
        }
      />

      <Select
        value={contentTypes}
        onChange={setContentTypes}
        options={SOURCE_CONTENT_TYPE_OPTIONS}
        style={{ width: '220px', marginBottom: '12px' }}
      />

      <Component.TransferComponent
        dataHandler={dataHandler}
        filterOption={filterOption}
        columns={columns}
      />
    </div>
  );
};

export default ContentRule;
